import React, { useEffect, useMemo, useState } from "react";
import DICOMViewer from "./DICOMViewer";
import { useViewer } from "./context/ViewerContext";
import spinner from "../../../assets/spiner.svg";

/** helpers */
function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function parseDateLoose(d) {
  if (!d) return null;
  // Support: ISO, Date, "YYYYMMDD", "YYYY-MM-DD"
  if (d instanceof Date) return d;
  if (typeof d === "number") return new Date(d);
  if (typeof d === "string") {
    const s = d.trim();
    if (/^\d{8}$/.test(s)) {
      // YYYYMMDD
      const y = +s.slice(0, 4);
      const m = +s.slice(4, 6) - 1;
      const day = +s.slice(6, 8);
      return new Date(y, m, day);
    }
    const asDate = new Date(s);
    if (!isNaN(asDate.getTime())) return asDate;
  }
  return null;
}

function formatAccqDate(dateLike) {
  const dt = parseDateLoose(dateLike);
  if (!dt) return "—";
  const day = ordinal(dt.getDate());
  const month = dt.toLocaleString("en-US", { month: "long" });
  const year = dt.getFullYear();
  // visual matches: "25th, September 2025"
  return `${day}, ${month} ${year}`;
}

export default function MultiViewDICOM({
  exams = [],
  currentUser,
  topLeftIndex,
  setTopLeftIndex,
}) {
  const tiles = useMemo(() => {
    if (!Array.isArray(exams)) return [];
    const count = Math.min(4, exams.length);
    return Array.from({ length: count }, (_, i) => exams[i]); // preserve placeholders
  }, [exams]);

  // global zoom from context (when it changes we clear local overrides)
  const { zoom: globalZoom } = useViewer();

  const [indices, setIndices] = useState(() =>
    Object.fromEntries(tiles.map((_, i) => [`slot-${i}`, 0]))
  );
  const setIdx = (key, v) => setIndices((prev) => ({ ...prev, [key]: v }));

  // Per-tile zoom override; null means "follow global"
  const [zoomMap, setZoomMap] = useState(() =>
    Object.fromEntries(tiles.map((_, i) => [`slot-${i}`, null]))
  );

  // ensure new tiles get a default entry
  useEffect(() => {
    setIndices((prev) => {
      const next = { ...prev };
      tiles.forEach((_, i) => {
        const key = `slot-${i}`;
        if (!(key in next)) next[key] = 0;
      });
      return next;
    });
    setZoomMap((prev) => {
      const next = { ...prev };
      tiles.forEach((_, i) => {
        const key = `slot-${i}`;
        if (!(key in next)) next[key] = null;
      });
      return next;
    });
  }, [tiles]);

  // When global zoom changes, reset all local overrides to follow it
  useEffect(() => {
    setZoomMap((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        next[k] = null;
      });
      return next;
    });
  }, [globalZoom]);

  const gridClass =
    tiles.length === 1
      ? "grid-cols-1 grid-rows-1"
      : tiles.length === 2
      ? "grid-cols-2 grid-rows-1"
      : "grid-cols-2 grid-rows-2";

  return (
    <div
      className={`grid ${gridClass} gap-2 p-2 w-full h-full min-w-0 min-h-0`}>
      {tiles.map((exam, i) => {
        const key = `slot-${i}`;
        const idx = i === 0 ? topLeftIndex : indices[key] ?? 0;
        const setIndex = i === 0 ? setTopLeftIndex : (v) => setIdx(key, v);
        const localTileZoom = zoomMap[key];
        const selectValue = String(localTileZoom ?? globalZoom);

        // Header fields
        const caseId = exam?.caseId ?? "N/A";
        const risId = exam?.patientId ?? "N/A";
        const dateRaw = exam?.createdAt;
        const dateLabel = formatAccqDate(dateRaw);
        const slideLabel = `Slide ${idx + 1}`;

        return (
          <div
            key={key}
            className="relative bg-black rounded overflow-hidden w-full h-full min-w-0 min-h-0 border border-[#2B2B2B]">
            {/* Header (black) */}
            <div className="flex items-center justify-between px-4 h-11 bg-black text-white border-b border-[#2B2B2B]">
              {/* Left: Slide | Date | Case - RIS */}
              <div className="flex items-center gap-3 text-[13px] leading-none">
                <span className="opacity-95">{slideLabel}</span>
                <span className="opacity-40">|</span>
                <span className="opacity-95">{dateLabel}</span>
                <span className="opacity-40">|</span>
                <span className="opacity-95">
                  Case {caseId} - RIS {risId}
                </span>
              </div>

              {/* Right: per-tile Zoom dropdown */}
              <div className="flex items-center gap-2">
                <select
                  className="text-xs px-2 py-1 rounded bg-[#151515] text-white border border-[#2B2B2B] outline-none"
                  value={selectValue}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setZoomMap((prev) => ({
                      ...prev,
                      [key]: isNaN(v) ? null : v,
                    }));
                  }}>
                  <option value={1}>100%</option>
                  <option value={1.25}>125%</option>
                  <option value={1.5}>150%</option>
                  <option value={2}>200%</option>
                  <option value={2.5}>250%</option>
                  <option value={3}>300%</option>
                </select>
              </div>
            </div>

            {/* Content area with clear divider from header */}
            <div className="absolute inset-x-0 bottom-0 top-11">
              {exam ? (
                <DICOMViewer
                  examData={exam}
                  currentUser={currentUser}
                  currentImageIndex={idx}
                  setCurrentImageIndex={setIndex}
                  notesOpen={false}
                  localZoom={localTileZoom ?? undefined}
                />
              ) : (
                <TileSkeleton label="Loading prior exam…" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TileSkeleton({ label = "Loading…" }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center z-20">
      <div className="flex flex-col items-center gap-2 text-gray-300 animate-pulse">
        <img
          src={spinner}
          alt="Loading…"
          className="h-16 w-16 animate-spin filter invert block"
        />
        <div className="text-sm">{label}</div>
      </div>
    </div>
  );
}
